import { useState, useEffect, useCallback } from 'react';
import {
  createTypesenseClient,
  getCollectionNames,
  getTypesenseSearchConfigError,
  TYPESENSE_SEARCH_CONFIG_ERROR,
} from '../utils/typesense';

/**
 * Custom hook for searching breweries via Typesense
 * Handles search, deleted brewery tracking, and state management
 * @param {string} usState - The US state to filter by
 * @param {string} searchQuery - The search query (should be debounced externally)
 * @param {string} deletedBreweryIdFromLocation - Optional deleted brewery ID from navigation state
 * @param {boolean} [claimedOnly=false] - When true, only breweries with hasBreweryAdmin in Typesense
 * @returns {Object} Object containing breweries, isLoading, error, deletedBreweryIds, and addDeletedBreweryId
 */
export function useBreweriesSearch(
  usState,
  searchQuery,
  deletedBreweryIdFromLocation,
  claimedOnly = false,
) {
  const [breweries, setBreweries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Track deleted brewery IDs per state (persists across Typesense refreshes)
  // Stored as { [usState]: string[] } so we only reconcile when we have a full fetch for that state
  const [deletedByState, setDeletedByState] = useState(() => {
    try {
      const stored = sessionStorage.getItem('breweries.deletedIds');
      const parsed = stored ? JSON.parse(stored) : null;
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed;
      }
      // Migrate legacy flat array to per-state format (legacy IDs used for all states)
      if (Array.isArray(parsed) && parsed.length > 0) {
        return { legacy: parsed };
      }
      return {};
    } catch {
      return {};
    }
  });

  // Flatten to array for display: current state + legacy (never reconciled)
  const deletedBreweryIds = [
    ...(deletedByState[usState] ?? []),
    ...(deletedByState.legacy ?? []),
  ];

  // Add deleted brewery ID from location to our tracking state (namespace by current usState)
  useEffect(() => {
    if (deletedBreweryIdFromLocation && usState) {
      setDeletedByState((prev) => {
        const stateIds = prev[usState] ?? [];
        if (!stateIds.includes(deletedBreweryIdFromLocation)) {
          const updated = {
            ...prev,
            [usState]: [...stateIds, deletedBreweryIdFromLocation],
          };
          try {
            sessionStorage.setItem('breweries.deletedIds', JSON.stringify(updated));
          } catch {
            // ignore storage errors
          }
          return updated;
        }
        return prev;
      });
    }
  }, [deletedBreweryIdFromLocation, usState]);

  // Fetch breweries via Typesense (filtered by state + search, sorted by name)
  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    async function fetchBreweries() {
      try {
        setError(null);

        const configError = getTypesenseSearchConfigError();
        if (configError) {
          setBreweries([]);
          setError(configError);
          setIsLoading(false);
          return;
        }

        const mode = (import.meta.env.MODE || 'development').toLowerCase();
        const env = ['development', 'staging', 'production'].includes(mode)
          ? mode
          : 'development';
        const { breweries: collectionName } = getCollectionNames(env);

        if (!collectionName) {
          setBreweries([]);
          setError(TYPESENSE_SEARCH_CONFIG_ERROR);
          setIsLoading(false);
          return;
        }

        const client = createTypesenseClient();
        if (!client) {
          setBreweries([]);
          setError(TYPESENSE_SEARCH_CONFIG_ERROR);
          setIsLoading(false);
          return;
        }

        // Typesense limits per_page to 250; paginate to fetch all results
        const perPage = 250;
        const MAX_PAGES = 100;
        const allHits = [];
        let page = 1;
        let found = 0;

        do {
          if (cancelled) break;

          const filterParts = [`state:=${usState}`];
          if (claimedOnly) {
            filterParts.push('hasBreweryAdmin:=true');
          }
          const filterBy = filterParts.join(' && ');

          const searchResponse = await client
            .collections(collectionName)
            .documents()
            .search({
              q: searchQuery || '*',
              query_by: 'name',
              filter_by: filterBy,
              sort_by: 'name:asc',
              per_page: perPage,
              page,
            });

          if (cancelled) break;

          const hits = searchResponse?.hits ?? [];
          if (hits.length === 0) break; // Safety: empty page, avoid infinite loop

          allHits.push(...hits);
          found = searchResponse?.found ?? 0;

          const maxPages = Math.max(1, Math.ceil(found / perPage));
          if (page >= maxPages || page >= MAX_PAGES) break;

          page += 1;
        } while (allHits.length < found);

        const hits = allHits;

        if (cancelled) return;

        const breweryIds = hits.map((h) => h.document?.id ?? h.id);
        setBreweries(
          hits.map((h) => {
            const doc = h.document ?? h;
            return {
              id: doc.id ?? h.id,
              data: doc,
            };
          }),
        );

        // Reconcile deleted IDs only after a full fetch for this usState: no query text,
        // same filter as Typesense-wide list (omit claimed-only subset).
        if (!cancelled && !searchQuery?.trim() && !claimedOnly) {
          setDeletedByState((prev) => {
            const stateIds = prev[usState] ?? [];
            if (stateIds.length === 0) return prev;
            const stillDeleted = stateIds.filter((id) => breweryIds.includes(id));
            if (stillDeleted.length === stateIds.length) return prev;
            const updated = { ...prev, [usState]: stillDeleted };
            try {
              if (stillDeleted.length > 0) {
                sessionStorage.setItem(
                  'breweries.deletedIds',
                  JSON.stringify(updated),
                );
              } else {
                const { [usState]: _, ...rest } = updated;
                sessionStorage.setItem(
                  'breweries.deletedIds',
                  JSON.stringify(Object.keys(rest).length > 0 ? rest : {}),
                );
              }
            } catch {
              // ignore storage errors
            }
            return updated;
          });
        }
      } catch (fetchError) {
        if (!cancelled) {
          console.error('Error fetching breweries from Typesense:', fetchError);
          setBreweries([]);
          setError(
            fetchError?.message ||
              'Failed to load breweries. Please try again.',
          );
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    fetchBreweries();
    return () => {
      cancelled = true;
    };
  }, [usState, searchQuery, claimedOnly]);

  /**
   * Manually add a deleted brewery ID to the tracking list (namespaced by current usState)
   * @param {string} breweryId - The ID of the deleted brewery
   */
  const addDeletedBreweryId = useCallback(
    (breweryId) => {
      if (!breweryId || !usState) return;
      setDeletedByState((prev) => {
        const stateIds = prev[usState] ?? [];
        if (stateIds.includes(breweryId)) return prev;
        const updated = {
          ...prev,
          [usState]: [...stateIds, breweryId],
        };
        try {
          sessionStorage.setItem('breweries.deletedIds', JSON.stringify(updated));
        } catch {
          // ignore storage errors
        }
        return updated;
      });
    },
    [usState],
  );

  return {
    breweries,
    isLoading,
    error,
    deletedBreweryIds,
    addDeletedBreweryId,
  };
}
